import { Typography } from "antd";
import styled from "styled-components/macro";

import Legend from "components/ui/Legend";

// EVA-6412 SR-005 (T042) — styled components hoisted verbatim out of
// GroupByFilterActivity.tsx so the thinned component is pure render + JSX.
// These are static (module-level) and are NOT "P4 shared mutable state" — no
// behavior change.
//
// NOTE: `styled(ActiveFilter)`/`styled(SavedFilterSelection)` are deliberately
// NOT here — `useGroupByFilterController.ts` (which needs `LegendContextMenuItem`
// from this file) is re-exported from the `./hooks` barrel, which `ActiveFilter.tsx`
// itself imports (for `usePropertyTitleLookup`). Wrapping `ActiveFilter` here would
// create styled.ts -> ActiveFilter.tsx -> hooks barrel -> useGroupByFilterController.ts
// -> styled.ts, a real import cycle that leaves `ActiveFilter` `undefined` at
// `styled()` call time. Those two wrappers stay colocated in GroupByFilterActivity.tsx
// (a leaf nothing else imports) instead.

const { Text } = Typography;

export const Wrapper = styled.div`
  height: calc(100vh - 45px);
  background: #ffffff;
  /* override white background for sections */
  --normalize-section-bg: #f4f6f6;
  --bin-section-bg: #f4f6f6;
  --active-filter-section-bg: #fcfcfc;
  display: grid;
  /* make Legend section take available space and
  ActiveFilter 300px max. when active */
  grid-template-rows: repeat(3, auto) 1fr minmax(auto, 270px);
  align-content: start;

  &.legend-off.filter-on {
    grid-template-rows: repeat(3, auto) auto 1fr;
  }

  &.filter-off {
    grid-template-rows: repeat(3, auto) 1fr auto;
  }

  .ant-spin {
    min-width: 40px;
  }

  & > div {
    overflow-y: hidden;
  }
`;

export const StyledText = styled(Text)`
  text-transform: uppercase;
  letter-spacing: 0.25px;
`;

export const GroupedLegendHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  /* -2px vertical margin compensates for Ant Design Panel's internal title padding
     (ant-collapse-header: padding 12px 16px, as of antd 4.x). Re-measure and
     adjust if the Panel component or antd version is upgraded. */
  margin: -2px 0;
`;

export const GroupedLegendHeaderTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

export const GroupedLegendHeaderBottom = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
`;

export const GroupByLabel = styled.span`
  font-size: 10px;
  color: #9ca3af;
  white-space: nowrap;
  flex-shrink: 0;
`;

export const GroupByValue = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #0d9488;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const GroupChangeTrigger = styled.button`
  font-size: 10px;
  color: #9ca3af;
  background: none;
  border: none;
  /* padding + negative margin expand the touch target to ~32px without
     changing the visual footprint. WCAG 2.5.5 recommends 44×44px minimum;
     32px is a practical compromise inside a constrained panel header. */
  padding: 4px 6px;
  margin: -4px -6px;
  margin-left: auto;
  cursor: pointer;
  text-decoration: underline;
  flex-shrink: 0;

  &:hover {
    color: #0d9488;
  }
`;

export const LegendList = styled.div<{ isPickerOpen: boolean }>`
  transition: opacity 150ms ease;
  ${({ isPickerOpen }) =>
    isPickerOpen &&
    `
    opacity: 0.4;
    pointer-events: none;
  `}
`;

export const LegendContent = styled.div<{ $isDisabled?: boolean }>`
  flex: 1;
  max-height: 100%;
  display: grid;
  grid-template-rows: 40px 1fr;
  overflow-y: hidden;
  ${({ $isDisabled }) =>
    $isDisabled &&
    `
    opacity: 0.6;
    pointer-events: none;
  `}
`;
export const LegendContentHeader = styled.div`
  height: 40px;
  display: grid;
  grid-template-columns: 32px 1fr repeat(5, 28px);
  padding: 0 15px;
  place-items: center;

  .BaseInput {
    height: 28px;
  }

  .ant-checkbox-wrapper {
    margin-right: 10px;
  }
`;
export const StyledLegend = styled(Legend)`
  flex-grow: 1;
  max-width: 100%;
  min-width: 100%;
  min-height: 0;
`;
export const LegendContextMenuItem = styled.div`
  min-width: 180px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--color-text-60);
  font-weight: var(--fontWeightMedium);
  padding: 6px 0;
  cursor: pointer;

  &:hover {
    color: var(--color-text);
  }

  &[aria-disabled="true"] {
    opacity: 0.4;
    cursor: not-allowed;

    &:hover {
      color: var(--color-text-60);
    }
  }
`;
export const ActiveFilterHeader = styled.div`
  flex-grow: 1;
  min-width: 0;
  min-height: 40px;
  max-height: 40px;
  display: flex;
  flex-flow: row;
  align-items: center;
  gap: 1px;

  .trigger-container:nth-child(1) {
    flex-grow: 1;
    min-width: 0;
  }
`;

export const ClearAllButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 2px 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 1.2rem;
  color: #b5b5b5;
  transition: color 150ms;
  flex-shrink: 0;
  text-decoration: underline;
  line-height: 1;
  padding-top: 3px;

  &:hover {
    color: var(--color-primary);
  }

  &:focus {
    outline: none;
  }

  svg {
    fill: currentColor;
  }
`;

export const ProductionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  text-transform: uppercase;
  letter-spacing: 0.25px;
  margin-right: 12px;
  white-space: nowrap;
  font-weight: 700;

  svg {
    color: gray;
    width: 15px;
    height: 15px;
  }
`;
